/*
 * Shared modern builds table used by Med and Lab domain views
 */
import { PATHS } from "../utils/constants";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@uhg-abyss/web/ui/DataTable";
import { Link, useHistory } from "react-router-dom";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Tooltip } from "@uhg-abyss/web/ui/Tooltip";
import { useDataTable } from "@uhg-abyss/web/hooks/useDataTable";
import { RadioGroup } from "@uhg-abyss/web/ui/RadioGroup";
import PropTypes from "prop-types";
import moment from "moment";

// Track currently-listened radios so we can enforce a single Begin and a single End at a time
let currentBeginRadio = null;
let currentEndRadio = null;

const formatBuildTimestamp = (value) =>
  value ? moment(value).format("MM-DD-YYYY HH:mm:ss") : "";

export default function ModernBuildTable({
  useDataHook,
  title = "Med Domain - Latest builds",
  detailsPath = PATHS.medDomainDetails.url,
  columnsType = "medDomain", // "medDomain", "medDomainEIS", or "labDomain"
  showDiffColumn = true,
  dataKey = "medDomainBuildRowData",
}) {
  const { state = {} } = useDataHook();
  const medDomainBuildRowData = state[dataKey];
  const data = medDomainBuildRowData ?? [];
  const history = useHistory();

  const [beginBuildId, setBeginBuildId] = useState(null);
  const [endBuildId, setEndBuildId] = useState(null);
  const [filterGroupId, setFilterGroupId] = useState(null);
  const [columnFilters, setColumnFilters] = useState([]);

  const canCompare =
    beginBuildId != null && endBuildId != null && beginBuildId !== endBuildId;

  const handleViewDiffClick = async () => {
    if (!canCompare || !filterGroupId) return;
    const params = new URLSearchParams({
      groupId: String(filterGroupId),
      newBuildId: String(endBuildId),
      oldBuildId: String(beginBuildId),
    });
    history.push(`${PATHS.medDomainDiff.url}?${params.toString()}`);
  };

  const resetDiffState = (type) => {
    if (type === "begin") {
      setBeginBuildId(null);
      setFilterGroupId(null);
      const beginRadios = document.querySelectorAll(
        'input[type="radio"].medDomain-begin-radio'
      );
      beginRadios.forEach((radio) => {
        radio.disabled = false;
      });
    } else if (type === "end") {
      setEndBuildId(null);
      const endRadios = document.querySelectorAll(
        'input[type="radio"].medDomain-end-radio'
      );
      endRadios.forEach((radio) => {
        radio.disabled = false;
      });
    } else {
      setBeginBuildId(null);
      setEndBuildId(null);
      setFilterGroupId(null);
      const beginRadios = document.querySelectorAll(
        'input[type="radio"].medDomain-begin-radio'
      );
      beginRadios.forEach((radio) => {
        radio.disabled = false;
      });
      const endRadios = document.querySelectorAll(
        'input[type="radio"].medDomain-end-radio'
      );
      endRadios.forEach((radio) => {
        radio.disabled = false;
      });
    }
  };

  function renderDatasourcesCell(cell) {
    const value = cell.getValue();
    if (!value) return null;
    const count = value.split(",").filter(Boolean).length;
    const tooltipContent = value
      .split(",")
      .filter(Boolean)
      .map((ds, idx) => <div key={idx}>{ds.trim()}</div>);
    const label = count === 1 ? "Data Source" : "Data Sources";
    return (
      <Tooltip content={tooltipContent}>
        <span>{`${count} ${label}`}</span>
      </Tooltip>
    );
  }

  function renderDiffCell(cell) {
    const row = cell.row.original;
    const rowBuildId = String(row.build_id);
    const rowGroupId = row.groupid;
    const [localBeginSelected, setLocalBeginSelected] = useState(false);
    const [localEndSelected, setLocalEndSelected] = useState(false);
    const beginGroupName = "diff-begin";
    const endGroupName = "diff-end";
    const handleBeginChange = (event) => {
      const { value } = event.target || {};
      if (!value) return;
      if (currentBeginRadio && currentBeginRadio !== event.target) {
        if (currentBeginRadio._nativeClickListener) {
          currentBeginRadio.removeEventListener(
            "click",
            currentBeginRadio._nativeClickListener
          );
          currentBeginRadio._nativeClickListener = null;
        }
        currentBeginRadio._nativeClickBound = false;
        currentBeginRadio.checked = false;
      }
      if (event && event.target && !event.target._nativeClickBound) {
        const target = event.target;
        const listener = (nativeEvent) => {
          const isCurrentlySelected = nativeEvent.currentTarget.ariaChecked;
          if (isCurrentlySelected) {
            setLocalBeginSelected(false);
            resetDiffState("begin");
            if (target._nativeClickListener) {
              target.removeEventListener("click", target._nativeClickListener);
              target._nativeClickListener = null;
            } else {
              target.removeEventListener("click", listener);
            }
            target._nativeClickBound = false;
            if (currentBeginRadio === target) {
              currentBeginRadio = null;
            }
            if (currentEndRadio) {
              try {
                const syntheticClick = new MouseEvent("click", {
                  bubbles: true,
                  cancelable: true,
                });
                currentEndRadio.dispatchEvent(syntheticClick);
              } catch (e) {
                currentEndRadio.click();
              }
            }
          }
        };
        target.addEventListener("click", listener);
        target._nativeClickBound = true;
        target._nativeClickListener = listener;
        currentBeginRadio = target;
        const allBeginRadios = document.querySelectorAll(
          'input[type="radio"].medDomain-begin-radio'
        );
        allBeginRadios.forEach((radio) => {
          if (radio !== target) {
            radio.disabled = true;
          }
        });
      } else {
        currentBeginRadio = event.target;
      }
      setLocalBeginSelected(true);
      const numericId = Number(rowBuildId);
      setBeginBuildId(numericId);
      setFilterGroupId(rowGroupId);
    };
    const handleEndChange = (event) => {
      const { value } = event.target || {};
      if (!value) return;
      if (currentEndRadio && currentEndRadio !== event.target) {
        if (currentEndRadio._nativeClickListener) {
          currentEndRadio.removeEventListener(
            "click",
            currentEndRadio._nativeClickListener
          );
          currentEndRadio._nativeClickListener = null;
        }
        currentEndRadio._nativeClickBound = false;
        currentEndRadio.checked = false;
      }
      if (event && event.target && !event.target._nativeClickBound) {
        const target = event.target;
        const listener = (nativeEvent) => {
          const isCurrentlySelected = nativeEvent.currentTarget.ariaChecked;
          if (isCurrentlySelected) {
            setLocalEndSelected(false);
            resetDiffState("end");
            if (target._nativeClickListener) {
              target.removeEventListener("click", target._nativeClickListener);
              target._nativeClickListener = null;
            } else {
              target.removeEventListener("click", listener);
            }
            target._nativeClickBound = false;
            if (currentEndRadio === target) {
              currentEndRadio = null;
            }
          }
        };
        target.addEventListener("click", listener);
        target._nativeClickBound = true;
        target._nativeClickListener = listener;
        currentEndRadio = target;
        const allEndRadios = document.querySelectorAll(
          'input[type="radio"].medDomain-end-radio'
        );
        allEndRadios.forEach((radio) => {
          if (radio !== target) {
            radio.disabled = true;
          }
        });
      } else {
        currentEndRadio = event.target;
      }
      setLocalEndSelected(true);
      const numericId = Number(rowBuildId);
      setEndBuildId(numericId);
    };
    return (
      <div className="d-flex align-items-center gap-2">
        <RadioGroup
          label="Begin"
          name={beginGroupName}
          value={localBeginSelected ? rowBuildId : ""}
          onChange={handleBeginChange}
        >
          <RadioGroup.Column>
            <RadioGroup.Radio
              value={rowBuildId}
              className="medDomain-begin-radio"
            />
          </RadioGroup.Column>
        </RadioGroup>
        <RadioGroup
          label="End"
          name={endGroupName}
          value={localEndSelected ? rowBuildId : ""}
          onChange={handleEndChange}
        >
          <RadioGroup.Column>
            <RadioGroup.Radio
              value={rowBuildId}
              className="medDomain-end-radio"
            />
          </RadioGroup.Column>
        </RadioGroup>
      </div>
    );
  }

  // --- COLUMN DEFINITIONS ---
  const medDomainColumns = [
    {
      header: "ID",
      meta: { headerLabel: "Build Id" },
      accessorKey: "build_id",
      cell: (cell) =>
        cell.getValue() ? (
          <Link
            to={{
              pathname: detailsPath.replace(":build_id", cell.getValue()),
              state: {
                groupid: cell.row.original.groupid,
                end_time: cell.row.original.end_time,
              },
            }}
          >
            {cell.getValue()}
          </Link>
        ) : null,
    },
    {
      header: "GroupID",
      accessorKey: "groupid",
      enableColumnFilter: true,
    },
    {
      header: "Build End Date",
      accessorKey: "end_time",
      cell: (cell) => formatBuildTimestamp(cell.getValue()),
    },
    {
      header: "User",
      accessorKey: "username",
    },
    {
      header: "Type",
      accessorKey: "build_type",
    },
    {
      header: "Status",
      accessorKey: "status",
    },
    {
      header: "Ref Version",
      accessorKey: "reference_vers",
    },
    {
      header: "Comment",
      accessorKey: "build_notes",
    },
    {
      header: "Datasources",
      accessorKey: "review_ds_list",
      cell: renderDatasourcesCell,
    },
  ];

  const medDomainEISColumns = [
    {
      header: "ID",
      accessorKey: "build_id",
      cell: (cell) => {
        const buildId = cell.row.original.build_id ?? cell.row.original.id;
        const correlationId = cell.row.original.correlation_id;
        return buildId && correlationId ? (
          <Link
            to={{
              pathname: detailsPath.replace(":correlation_id", correlationId),
              state: {
                end_time: cell.row.original.end_time,
                correlation_id: cell.row.original.correlation_id,
              },
            }}
          >
            {buildId}
          </Link>
        ) : null;
      },
    },
    {
      header: "Correlation ID",
      accessorKey: "correlation_id",
    },
    {
      header: "Start Time",
      accessorKey: "start_time",
      cell: (cell) => formatBuildTimestamp(cell.getValue()),
    },
    {
      header: "End Time",
      accessorKey: "end_time",
      cell: (cell) => formatBuildTimestamp(cell.getValue()),
    },
    {
      header: "Success",
      accessorKey: "success",
    },
    {
      header: "Result",
      accessorKey: "result",
    },
    {
      header: "Error",
      accessorKey: "error",
    },
    {
      header: "Client ID",
      accessorKey: "client_id",
    },
    {
      header: "Time (s)",
      accessorKey: "time_taken_in_seconds",
    },
    {
      header: "Num Recs",
      accessorKey: "num_recs",
    },
    {
      header: "Num LMC",
      accessorKey: "num_lmc",
    },
    {
      header: "TTL Map",
      accessorKey: "ttl_map",
    },
    {
      header: "Fixed",
      accessorKey: "fixed",
    },
    {
      header: "NDC",
      accessorKey: "ndc",
    },
    {
      header: "RxNorm",
      accessorKey: "rxnorm",
    },
    {
      header: "Addon",
      accessorKey: "addon",
    },
    {
      header: "Missed",
      accessorKey: "missed",
    },
    {
      header: "Local Source System",
      accessorKey: "local_source_system",
      cell: (cell) =>
        Array.isArray(cell.getValue())
          ? cell.getValue().join(", ")
          : cell.getValue(),
    },
  ];

  const labDomainColumns = [
    {
      header: "ID",
      meta: { headerLabel: "Build Id" },
      accessorKey: "id",
    },
    {
      header: "Correlation ID",
      accessorKey: "correlation_id",
      enableColumnFilter: true,
    },
    {
      header: "Start Time",
      accessorKey: "start_time",
      cell: (cell) => formatBuildTimestamp(cell.getValue()),
    },
    {
      header: "End Time",
      accessorKey: "end_time",
      cell: (cell) => formatBuildTimestamp(cell.getValue()),
    },
    {
      header: "Success",
      accessorKey: "success",
    },
    {
      header: "Result",
      accessorKey: "result",
    },
    {
      header: "Error",
      accessorKey: "build_problems",
    },
    {
      header: "Client ID",
      accessorKey: "client_id",
    },
    {
      header: "Time (s)",
      accessorKey: "time_taken_in_seconds",
    },
    {
      header: "Num Recs",
      accessorKey: "num_recs",
    },
    {
      header: "Num LMC",
      accessorKey: "num_lmc",
    },
    {
      header: "Local Source System",
      accessorKey: "local_source_system",
      cell: (cell) =>
        Array.isArray(cell.getValue())
          ? cell.getValue().join(", ")
          : cell.getValue(),
    },
  ];

  const diffColumn = {
    header: "Diff",
    id: "diffSelection",
    cell: renderDiffCell,
  };

  const columns = useMemo(() => {
    let baseCols;
    if (columnsType === "medDomainEIS") {
      baseCols = medDomainEISColumns;
    } else if (columnsType === "labDomain") {
      baseCols = labDomainColumns;
    } else {
      baseCols = medDomainColumns;
    }
    return showDiffColumn ? [...baseCols, diffColumn] : baseCols;
  }, [columnsType, beginBuildId, endBuildId, showDiffColumn]);

  useEffect(() => {
    if (filterGroupId == null) {
      setColumnFilters((prev) => prev.filter((f) => f.id !== "groupid"));
    } else {
      setColumnFilters((prev) => {
        const withoutGroup = prev.filter((f) => f.id !== "groupid");
        const newFilter = {
          id: "groupid",
          value: {
            matchType: "all",
            filters: [
              {
                value: String(filterGroupId),
                condition: "contains",
              },
            ],
          },
        };
        return [...withoutGroup, newFilter];
      });
    }
  }, [filterGroupId]);

  const dataTableProps = useDataTable({
    initialData: data,
    initialColumns: columns,
    tableConfig: {
      enableColumnFilters: true,
      state: {
        columnFilters,
      },
      onColumnFiltersChange: setColumnFilters,
    },
  });

  useEffect(() => {
    try {
      if (
        Array.isArray(medDomainBuildRowData) &&
        typeof dataTableProps.setData === "function"
      ) {
        dataTableProps.setData(medDomainBuildRowData, true);
      }
    } catch (e) {
      console.error("Error in setData useEffect:", e);
    }
  }, [medDomainBuildRowData, dataTableProps]);

  return (
    <div className="grid">
      <div className="mb-4 d-flex justify-content-end gap-3">
        <Button>Latest Builds</Button>
        <Button>Execute Builds</Button>
        <Button>Monitor</Button>
      </div>
      <div className="row mb-4">
        <DataTable title={title} tableState={dataTableProps}>
          <DataTable.Table />
        </DataTable>
      </div>
      {showDiffColumn && (
        <div className="d-flex flex-grow-1 m-4">
          <div className="d-flex flex-grow-1 justify-content-end">
            <Button isDisabled={!canCompare} onClick={handleViewDiffClick}>
              View Diff
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

ModernBuildTable.propTypes = {
  useDataHook: PropTypes.func.isRequired,
  title: PropTypes.string,
  detailsPath: PropTypes.string,
  columnsType: PropTypes.string,
  showDiffColumn: PropTypes.bool,
  dataKey: PropTypes.string,
};
